import { useEffect, useState } from "react";
import { RAIL_FOLDER_ICONS, sanitiseRailFolderIcon, type MyCommunity, type RailFolder } from "@atrium/shared";
import { cn } from "../../lib/cn";
import { ROLE_COLOURS } from "../../lib/palette";
import { Dialog } from "../../components/ui/Dialog";
import { Button } from "../../components/ui/Button";
import { Field, Input } from "../../components/ui/Input";
import { Tooltip } from "../../components/ui/Tooltip";
import { IconCheck, IconWidget } from "../../components/icons";
import { FOLDER_ICONS, FolderSymbol, FolderTile, folderTint } from "./FolderGlyph";
import { folderLabel } from "./useRail";

type Draft = Pick<RailFolder, "name" | "color" | "icon">;

const HEX = /^#[0-9a-fA-F]{6}$/;

/** Name, colour and icon for a rail folder. */
export function FolderDialog({
  folder,
  communities,
  open,
  onOpenChange,
  onSave,
}: {
  folder: RailFolder | null;
  communities: MyCommunity[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (draft: Draft) => void;
}) {
  const [draft, setDraft] = useState<Draft>({ name: "", color: null, icon: null });
  const [hex, setHex] = useState("");
  const [emoji, setEmoji] = useState("");
  useEffect(() => {
    if (!open || !folder) return;
    setDraft({ name: folder.name, color: folder.color, icon: folder.icon });
    setHex(folder.color ?? "");
    setEmoji(folder.icon && !(folder.icon in FOLDER_ICONS) ? folder.icon : "");
  }, [open, folder?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const setColor = (color: string | null) => {
    setDraft((d) => ({ ...d, color }));
    setHex(color ?? "");
  };
  const tint = folderTint(draft.color);
  const isEmoji = !!draft.icon && !(draft.icon in FOLDER_ICONS);
  const hexInvalid = hex !== "" && !HEX.test(hex);
  const emojiInvalid = emoji.trim() !== "" && !sanitiseRailFolderIcon(emoji);

  const save = () => {
    onSave({ name: draft.name.trim(), color: draft.color, icon: draft.icon });
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Folder settings"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="rail-folder-form" disabled={hexInvalid}>
            Save
          </Button>
        </>
      }
    >
      <form
        id="rail-folder-form"
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (!hexInvalid) save();
        }}
      >
        <div className="flex items-end gap-3">
          <FolderTile folder={draft} communities={communities} className="mb-0.5 shrink-0" />
          <Field label="Name" className="flex-1">
            {(p) => <Input {...p} value={draft.name} maxLength={32} placeholder={folderLabel("", communities)} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />}
          </Field>
        </div>

        <div>
          <div className="mb-2 text-sm font-medium text-fg-2">Colour</div>
          <div role="radiogroup" aria-label="Folder colour" className="flex flex-wrap items-center gap-2">
            <Tooltip label="Accent colour">
              <button
                type="button"
                role="radio"
                aria-checked={draft.color === null}
                aria-label="Accent colour"
                onClick={() => setColor(null)}
                className={cn("flex size-7 items-center justify-center rounded-full bg-accent", draft.color === null && "ring-2 ring-fg ring-offset-2 ring-offset-bg-subtle")}
              >
                {draft.color === null && <IconCheck className="icon size-4 text-accent-fg" aria-hidden />}
              </button>
            </Tooltip>
            {ROLE_COLOURS.map((c) => (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={draft.color === c}
                aria-label={c}
                onClick={() => setColor(c)}
                className={cn("flex size-7 items-center justify-center rounded-full", draft.color === c && "ring-2 ring-fg ring-offset-2 ring-offset-bg-subtle")}
                style={{ background: c }}
              >
                {draft.color === c && <IconCheck className="icon size-4 text-white" aria-hidden />}
              </button>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Tooltip label="Pick any colour">
              <label className="relative size-7 shrink-0 cursor-pointer overflow-hidden rounded-full border border-line-strong" style={{ background: draft.color ?? "transparent" }}>
                <span className="sr-only">Pick any colour</span>
                <input type="color" value={draft.color ?? "#7c93ff"} onChange={(e) => setColor(e.target.value.toLowerCase())} className="absolute inset-0 cursor-pointer opacity-0" />
              </label>
            </Tooltip>
            <Input
              value={hex}
              onChange={(e) => {
                const v = e.target.value.trim();
                setHex(v);
                if (HEX.test(v)) setDraft((d) => ({ ...d, color: v.toLowerCase() }));
                else if (v === "") setDraft((d) => ({ ...d, color: null }));
              }}
              placeholder="#hex"
              aria-label="Hex colour"
              aria-invalid={hexInvalid}
              maxLength={7}
              className="!h-8 max-w-32 font-mono text-sm"
            />
          </div>
        </div>

        <div>
          <div className="mb-2 text-sm font-medium text-fg-2">Icon</div>
          <div role="radiogroup" aria-label="Folder icon" className="grid grid-cols-[repeat(auto-fill,minmax(2.5rem,1fr))] gap-1.5">
            <Tooltip label="Community icons">
              <button
                type="button"
                role="radio"
                aria-checked={draft.icon === null}
                aria-label="Community icons"
                onClick={() => {
                  setDraft({ ...draft, icon: null });
                  setEmoji("");
                }}
                className={cn("flex size-10 items-center justify-center rounded-lg text-fg-2 hover:bg-surface", draft.icon === null && "bg-surface-2 ring-2 ring-accent")}
              >
                <IconWidget className="icon size-5" aria-hidden />
              </button>
            </Tooltip>
            {RAIL_FOLDER_ICONS.map((key) => (
              <Tooltip key={key} label={FOLDER_ICONS[key].label}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={draft.icon === key}
                  aria-label={FOLDER_ICONS[key].label}
                  onClick={() => {
                    setDraft({ ...draft, icon: key });
                    setEmoji("");
                  }}
                  className={cn("flex size-10 items-center justify-center rounded-lg hover:bg-surface", draft.icon === key ? "bg-surface-2 ring-2 ring-accent" : "text-fg-2")}
                >
                  <FolderSymbol icon={key} className="size-5" style={draft.icon === key ? tint.fg : undefined} />
                </button>
              </Tooltip>
            ))}
          </div>
          <Field label="Or an emoji" className="mt-3" error={emojiInvalid ? "Use a single emoji" : null}>
            {(p) => (
              <Input
                {...p}
                value={emoji}
                maxLength={16}
                className={cn("max-w-32 text-lg", isEmoji && "border-accent")}
                onChange={(e) => {
                  const v = e.target.value;
                  setEmoji(v);
                  const ok = sanitiseRailFolderIcon(v);
                  if (ok && !(ok in FOLDER_ICONS)) setDraft((d) => ({ ...d, icon: ok }));
                  else if (!v.trim() && isEmoji) setDraft((d) => ({ ...d, icon: null }));
                }}
              />
            )}
          </Field>
        </div>
      </form>
    </Dialog>
  );
}
